import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, Clock, MessageSquare, Users, Headphones, Calendar } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLocale } from '../utils/localization';

const ContactPage = () => {
  const { currency, formatPrice } = useCurrency();
  const { locale } = useLocale(currency);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Thank you for your message! We\'ll get back to you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      business: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Call Us - UK",
      description: "United Kingdom",
      value: "+44 7830 354800",
      action: "tel:+447830354800"
    },
    {
      icon: Phone,
      title: "Call Us - USA",
      description: "Toll Free",
      value: "+1 866-540-6506",
      action: "tel:+18665406506"
    },
    {
      icon: MessageSquare,
      title: "Live Chat",
      description: "Chat with support",
      value: "Available 9am-6pm GMT",
      action: "#"
    },
    {
      icon: Calendar,
      title: "Book a Demo",
      description: "See the system in action",
      value: "30-minute demo call",
      action: "https://app.localbusinessheroes.net/"
    }
  ];

  const supportOptions = [
    {
      icon: Users,
      title: "Sales Enquiries",
      description: "Questions about plans and pricing",
      hours: "9am - 6pm GMT, Mon-Fri"
    },
    {
      icon: Headphones,
      title: "Technical Support",
      description: "Help with your account and features",
      hours: "9am - 9pm GMT, 7 days a week"
    },
    {
      icon: MessageSquare,
      title: "General Enquiries",
      description: "Any other questions or feedback",
      hours: "We respond within 24 hours"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Talk to a
            <span className="text-[#FF5A3D]"> Hero</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            Have questions? Need help choosing the right plan? Want to see a demo? 
            Our team is here to help you grow your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              asChild
              className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="/pricing">
                Skip the Chat - Start Your {formatPrice(5)} Trial
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#0E163B] px-8 py-4 rounded-xl">
              <a href="#contact-form">Send Us a Message</a>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <Clock size={48} className="text-[#FF5A3D] mx-auto mb-3" />
              <div className="font-semibold mb-1">Quick Response</div>
              <div className="text-sm text-gray-400">We respond to all enquiries within 24 hours</div>
            </div>
            <div className="text-center">
              <Users size={48} className="text-[#FF5A3D] mx-auto mb-3" />
              <div className="font-semibold mb-1">Real People</div>
              <div className="text-sm text-gray-400">Talk to actual business owners, not chatbots</div>
            </div>
            <div className="text-center">
              <Headphones size={48} className="text-[#FF5A3D] mx-auto mb-3" />
              <div className="font-semibold mb-1">Expert Support</div>
              <div className="text-sm text-gray-400">Get help from people who understand local business</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full mb-6">
              Get In Touch
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Choose How You'd Like to Connect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer multiple ways to get in touch, so you can choose what works best for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#FF5A3D] group-hover:text-white transition-colors">
                    <method.icon size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0E163B] mb-2">{method.title}</h3>
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  <div className="font-medium text-[#FF5A3D] mb-4">{method.value}</div>
                  <Button 
                    asChild
                    className="w-full bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white"
                  >
                    <a href={method.action} target={method.action.startsWith('http') ? '_blank' : undefined} rel={method.action.startsWith('http') ? 'noopener noreferrer' : undefined}>
                      {method.title.includes('Email') ? 'Send Email' : 
                       method.title.includes('Call') ? 'Call Now' :
                       method.title.includes('Chat') ? 'Start Chat' : 'Book Demo'}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Hours */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
              Support Hours
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              We're Here When You Need Us
            </h2>
            <p className="text-xl text-gray-600">
              Different types of support available at different times
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportOptions.map((option, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-[#FF5A3D]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <option.icon size={32} className="text-[#FF5A3D]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0E163B] mb-3">{option.title}</h3>
                  <p className="text-gray-600 mb-4">{option.description}</p>
                  <Badge className="bg-green-100 text-green-800">{option.hours}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-green-100 text-green-800 px-4 py-2 rounded-full mb-6">
              Send a Message
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Drop Us a Line
            </h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          <Card className="p-8 shadow-lg">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="business">Business Name</Label>
                    <Input
                      id="business"
                      value={formData.business}
                      onChange={(e) => handleInputChange('business', e.target.value)}
                      placeholder="Your business name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder={locale.phoneExample}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Select value={formData.subject} onValueChange={(value) => handleInputChange('subject', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="What can we help you with?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">Sales Enquiry - I want to know more about pricing</SelectItem>
                      <SelectItem value="demo">Demo Request - I'd like to see how it works</SelectItem>
                      <SelectItem value="support">Technical Support - I need help with something</SelectItem>
                      <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                      <SelectItem value="feedback">Feedback or Suggestion</SelectItem>
                      <SelectItem value="other">Other - General Enquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell us more about how we can help you..."
                    rows={6}
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold py-3"
                  >
                    Send Message
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    className="flex-1 border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white py-3"
                  >
                    <a href="/pricing">
                      Or Start Your {formatPrice(5)} Trial Now
                    </a>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Location & Additional Info */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h3 className="text-2xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone size={20} className="text-[#FF5A3D] mt-1" />
                  <div>
                    <div className="font-medium">Phone - UK</div>
                    <div className="text-gray-600">+44 7830 354800</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone size={20} className="text-[#FF5A3D] mt-1" />
                  <div>
                    <div className="font-medium">Phone - USA (Toll Free)</div>
                    <div className="text-gray-600">+1 866-540-6506</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin size={20} className="text-[#FF5A3D] mt-1" />
                  <div>
                    <div className="font-medium">Location</div>
                    <div className="text-gray-600">United Kingdom</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock size={20} className="text-[#FF5A3D] mt-1" />
                  <div>
                    <div className="font-medium">Business Hours</div>
                    <div className="text-gray-600 text-sm">
                      Mon-Fri: 9am - 6pm GMT<br />
                      Sat-Sun: 10am - 4pm GMT
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Preview */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Quick Answers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-[#0E163B] mb-2">How quickly can I get started?</h4>
                    <p className="text-sm text-gray-600">You can start your 14-day trial immediately for just {formatPrice(5)}. Most businesses are up and running within 30 minutes.</p>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-[#0E163B] mb-2">Do you offer setup help?</h4>
                    <p className="text-sm text-gray-600">Yes! All Growth and Domination plans include setup assistance from our team.</p>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-[#0E163B] mb-2">Can I cancel anytime?</h4>
                    <p className="text-sm text-gray-600">Absolutely. No contracts, no cancellation fees. You can cancel your subscription anytime.</p>
                  </CardContent>
                </Card>
                <Card className="p-4">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-[#0E163B] mb-2">What if I need more features?</h4>
                    <p className="text-sm text-gray-600">You can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Why wait? Start your 14-day trial now and see how Local Business Heroes can transform your growth.
          </p>
          
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-10 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <a href="/pricing">
              Start Your 14-Day Trial for {formatPrice(5)}
            </a>
          </Button>
          
          <p className="text-sm opacity-75 mt-6">
            Just {formatPrice(5)} to start · Cancel anytime · Full support included
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;